import React, { useMemo, useState } from "react";
import type { TopologyDevice, TopologyHub, HealthView } from "../lib/topologyAdapter";
import { computeTopologyLayout, CHIP_H } from "../lib/topologyLayout";
import { HEALTH_PILL_CONFIG } from "./HealthPill";

const HOST_X = 24;
const HOST_W = 150;
const HOST_BOX_H = 80;
const HUB_X = 372;
const HUB_W = 220;
const HUB_BOX_H = 60;
const PHONE_X = 700;
const PHONE_W = 320;
const RIGHT_PAD = 24;

const TOTAL_WIDTH = PHONE_X + PHONE_W + RIGHT_PAD;
const MIN_WIDTH = 900;

function bezier(x1: number, y1: number, x2: number, y2: number): string {
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
}

// Host -> hub edge color follows hub health — same palette as HealthPill so
// the wire's color always agrees with the hub card's pill.
function hostEdgeStyle(health: HealthView): { stroke: string; dash?: string } {
  if (health === "good") return { stroke: "var(--color-neutral)" };
  if (health === "unknown") return { stroke: "var(--color-neutral)", dash: "4 4" };
  return { stroke: HEALTH_PILL_CONFIG[health].color };
}

interface EdgeStyle {
  stroke: string;
  width: number;
  dash?: string;
  opacity: number;
}

// Hub -> phone edge styling by link-speed bucket + status: dashed/red for
// offline, dashed/amber for a degraded (full/low speed) link, solid neutral
// for a healthy link.
function deviceEdgeStyle(device: TopologyDevice): EdgeStyle {
  if (device.isOffline) {
    return { stroke: "var(--color-status-offline-text)", width: 1.5, dash: "4 4", opacity: 0.55 };
  }
  if (device.isDegraded) {
    return { stroke: "var(--color-status-busy-text)", width: 1.5, dash: "5 4", opacity: 0.9 };
  }
  return { stroke: "var(--color-neutral)", width: 2.5, opacity: 0.9 };
}

function phoneAccentColor(device: TopologyDevice): string {
  if (device.isOffline) return "var(--color-status-offline-text)";
  if (device.isDegraded) return "var(--color-status-busy-text)";
  return "var(--color-status-available-text)";
}

function phoneBadgeLabel(device: TopologyDevice): string {
  if (device.isOffline) return "✕ OFFLINE"; // ✕ OFFLINE
  if (device.isDegraded) return `▲ ${device.linkSpeedRaw ?? "degraded"}`; // ▲ ...
  return device.linkSpeedRaw ?? "—"; // —
}

export interface TopologyGraphProps {
  hostname: string;
  ipAddress: string;
  hubs: TopologyHub[];
  devices: TopologyDevice[];
  onSelectDevice: (device: TopologyDevice) => void;
}

/**
 * Inline SVG fan-out: host -> hubs -> phones. Ports the layout algorithm
 * from the accepted mockup via computeTopologyLayout(): each hub is
 * centered on the mean of its devices, the host is centered on the mean of
 * the hubs. Height is dynamic (grows with device count) — never fixed or
 * zero-size. Horizontal overflow scrolls rather than squeezing nodes.
 */
export default function TopologyGraph({
  hostname,
  ipAddress,
  hubs,
  devices,
  onSelectDevice,
}: TopologyGraphProps) {
  const [hoveredSerial, setHoveredSerial] = useState<string | null>(null);

  const layout = useMemo(
    () =>
      computeTopologyLayout(
        hubs.map((h) => ({ id: h.id })),
        devices.map((d) => ({ serial: d.serial, hubId: d.hubId }))
      ),
    [hubs, devices]
  );

  // Never render a 0-size SVG — when there's no topology to draw at all,
  // show an honest empty note instead.
  if (hubs.length === 0) {
    return (
      <p
        style={{
          color: "var(--color-text-secondary)",
          fontSize: "var(--font-size-sm)",
          padding: "var(--space-md) 0",
        }}
      >
        No USB hub topology reported yet — the agent&rsquo;s USB scan hasn&rsquo;t
        completed for this host.
      </p>
    );
  }

  const hubById = new Map(hubs.map((h) => [h.id, h]));
  // Layout nodes only carry a serial (pure geometry, decoupled from the
  // view-model). Resolve back to the full device record — including its
  // real id — by matching serial against the already-fetched devices array.
  const deviceBySerial = new Map(devices.map((d) => [d.serial, d]));

  const height = Math.max(layout.height, HOST_BOX_H + 20);

  return (
    <div style={{ overflowX: "auto" }}>
      <svg
        role="img"
        aria-label={`USB topology fan-out from ${hostname} to ${hubs.length} hub${
          hubs.length === 1 ? "" : "s"
        } to ${devices.length} device${devices.length === 1 ? "" : "s"}`}
        width={TOTAL_WIDTH}
        height={height}
        viewBox={`0 0 ${TOTAL_WIDTH} ${height}`}
        style={{ display: "block", minWidth: MIN_WIDTH }}
      >
        {/* host -> hub edges */}
        {layout.hubs.map((hubLayout) => {
          const hub = hubById.get(hubLayout.id);
          if (!hub) return null;
          const style = hostEdgeStyle(hub.health);
          return (
            <path
              key={`host-edge-${hub.id}`}
              d={bezier(HOST_X + HOST_W, layout.hostCY, HUB_X, hubLayout.cy)}
              fill="none"
              stroke={style.stroke}
              strokeWidth={3.5}
              strokeDasharray={style.dash}
              opacity={0.85}
            />
          );
        })}

        {/* hub -> phone edges */}
        {layout.devices.map((devLayout) => {
          const device = deviceBySerial.get(devLayout.serial);
          const hubLayout = layout.hubs.find((h) => h.id === devLayout.hubId);
          if (!device || !hubLayout) return null;
          const style = deviceEdgeStyle(device);
          const isHovered = hoveredSerial === device.serial;
          return (
            <path
              key={`dev-edge-${device.id}`}
              d={bezier(HUB_X + HUB_W, hubLayout.cy, PHONE_X, devLayout.cy)}
              fill="none"
              stroke={isHovered ? "var(--color-info)" : style.stroke}
              strokeWidth={isHovered ? style.width + 1.5 : style.width}
              strokeDasharray={style.dash}
              opacity={isHovered ? 1 : style.opacity}
            />
          );
        })}

        {/* host node */}
        <g>
          <rect
            x={HOST_X}
            y={layout.hostCY - HOST_BOX_H / 2}
            width={HOST_W}
            height={HOST_BOX_H}
            rx={8}
            fill="var(--color-primary)"
          />
          <text
            x={HOST_X + HOST_W / 2}
            y={layout.hostCY - 10}
            fill="#fff"
            fontSize={13}
            fontWeight={700}
            textAnchor="middle"
          >
            {hostname}
          </text>
          <text
            x={HOST_X + HOST_W / 2}
            y={layout.hostCY + 8}
            fill="#bcd4e2"
            fontSize={10}
            textAnchor="middle"
          >
            Host
          </text>
          <text
            x={HOST_X + HOST_W / 2}
            y={layout.hostCY + 24}
            fill="#8fb6cd"
            fontSize={10}
            textAnchor="middle"
          >
            {ipAddress}
          </text>
        </g>

        {/* hub nodes */}
        {layout.hubs.map((hubLayout) => {
          const hub = hubById.get(hubLayout.id);
          if (!hub) return null;
          const y = hubLayout.cy - HUB_BOX_H / 2;
          const cfg = HEALTH_PILL_CONFIG[hub.health];
          return (
            <g key={`hub-node-${hub.id}`}>
              <rect
                x={HUB_X}
                y={y}
                width={HUB_W}
                height={HUB_BOX_H}
                rx={8}
                fill="var(--color-surface)"
                stroke="var(--color-divider)"
              />
              <rect x={HUB_X} y={y} width={5} height={HUB_BOX_H} rx={2} fill={cfg.color} />
              <text x={HUB_X + 16} y={y + 20} fontSize={13} fontWeight={700} fill="var(--color-primary)">
                {hub.name}
              </text>
              <text x={HUB_X + 16} y={y + 36} fontSize={10.5} fill="var(--color-text-secondary)">
                {hubLayout.count} device{hubLayout.count === 1 ? "" : "s"}
              </text>
              <text
                x={HUB_X + HUB_W - 14}
                y={y + 20}
                fontSize={10.5}
                fontWeight={700}
                fill={cfg.color}
                textAnchor="end"
              >
                {cfg.glyph} {cfg.label}
              </text>
            </g>
          );
        })}

        {/* phone chips */}
        {layout.devices.map((devLayout) => {
          const device = deviceBySerial.get(devLayout.serial);
          if (!device) return null;
          const accent = phoneAccentColor(device);
          const lowConfidence = device.lowConfidenceMatch;
          const label = `${device.model} ${device.serial} — ${
            device.isOffline ? "offline" : device.isDegraded ? "degraded link" : "healthy"
          }${lowConfidence ? " — low-confidence hub match" : ""}`;
          return (
            <g
              key={`phone-${device.id}`}
              role="button"
              tabIndex={0}
              aria-label={label}
              onMouseEnter={() => setHoveredSerial(device.serial)}
              onMouseLeave={() => setHoveredSerial(null)}
              onClick={() => onSelectDevice(device)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelectDevice(device);
              }}
              style={{ cursor: "pointer" }}
            >
              <title>{label}</title>
              <rect
                x={PHONE_X}
                y={devLayout.yTop}
                width={PHONE_W}
                height={CHIP_H}
                rx={5}
                fill="var(--color-surface)"
                stroke={lowConfidence ? "var(--color-warning)" : "var(--color-divider)"}
                strokeDasharray={lowConfidence ? "3 2" : undefined}
              />
              <rect x={PHONE_X} y={devLayout.yTop} width={4} height={CHIP_H} rx={2} fill={accent} />
              <text
                x={PHONE_X + 12}
                y={devLayout.yTop + 15}
                fontSize={11}
                fontWeight={600}
                fill="var(--color-text-primary)"
              >
                {device.model}
              </text>
              <text
                x={PHONE_X + 108}
                y={devLayout.yTop + 15}
                fontSize={9.5}
                fill="var(--color-text-secondary)"
              >
                {device.serial}
              </text>
              {lowConfidence && (
                <text
                  x={PHONE_X + PHONE_W - 78}
                  y={devLayout.yTop + 15}
                  fontSize={10}
                  fontWeight={700}
                  fill="var(--color-warning)"
                  textAnchor="end"
                >
                  ?
                </text>
              )}
              <text
                x={PHONE_X + PHONE_W - 10}
                y={devLayout.yTop + 15}
                fontSize={9.5}
                fontWeight={700}
                fill={accent}
                textAnchor="end"
              >
                {phoneBadgeLabel(device)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
